import { defineComponent as _defineComponent } from 'vue';
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withModifiers as _withModifiers, withKeys as _withKeys, createElementVNode as _createElementVNode, withDirectives as _withDirectives, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, unref as _unref } from "vue";
const _hoisted_1 = { class: "search-filters__main" };
const _hoisted_2 = {
    key: 0,
    class: "search-filters__input-wrapper"
};
const _hoisted_3 = ["placeholder"];
const _hoisted_4 = {
    key: 0,
    class: "search-properties search-properties--matching"
};
const _hoisted_5 = ["onClick"];
const _hoisted_6 = { class: "search-properties__type" };
const _hoisted_7 = { class: "search-filters__filters" };
const _hoisted_8 = {
    key: 0,
    class: "search-filters__dropdown"
};
const _hoisted_9 = { class: "search-filters__dropdown-title" };
const _hoisted_10 = { class: "search-properties search-properties--saved" };
const _hoisted_11 = ["onClick"];
const _hoisted_12 = ["onClick"];
const _hoisted_13 = ["onClick"];
const _hoisted_14 = { class: "search-properties__type" };
const _hoisted_15 = {
    key: 1,
    class: "search-filters__filters-actions"
};
const _hoisted_16 = ["title"];
const _hoisted_17 = ["title"];
import { onMounted, ref, watch, computed } from 'vue';
import { cloneDeep as _cloneDeep, debounce as _debounce } from 'lodash';
import Icon from "./Icon.vue";
import SearchFilterActive from "./SearchFilterActive.vue";
import SearchFiltersPrimaryFilter from "./SearchFiltersPrimaryFilter.vue";
import SavedSearchService from "../services/SavedSearchService";
import env from "../services/AppEnv";
import SearchSaveModal from "./SearchSaveModal.vue";
import { useClickaway } from "../composables/clickaway";
import { searchFilterOperatorTakesValue } from "../util/SearchUtils";
export default /*@__PURE__*/ _defineComponent({
    __name: 'SearchFilters',
    props: {
        name: {},
        filters: {},
        noSearchQuery: { type: Boolean },
        searchRequest: {},
        searchQueryPlaceholder: {},
        disableFilterControls: { type: Boolean }
    },
    emits: [
        'update'
    ],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const root = ref(null);
        const searchFiltersDropdown = ref(null);
        const availableFilters = ref([]);
        const activeFilters = ref([]);
        const availablePrimaryFilter = ref(null);
        const activePrimaryFilter = ref(null);
        const searchQuery = ref(null);
        const selectedFilter = ref(null);
        const active = ref(false);
        const filtered = computed(() => isFiltered());
        const showAvailableFiltersDropdown = ref(false);
        const matchingFilters = ref([]);
        const matchingFilterHighlighted = ref(null);
        const savedSearchService = ref(null);
        const showSaveModal = ref(false);
        const savedSearchRequests = ref([]);
        const searchInput = ref(null);
        const activeFiltersRefs = ref({});
        onMounted(() => useClickaway(root, blur));
        onMounted(() => useClickaway(searchFiltersDropdown, hideAvailableFiltersDropdown));
        const updateSearchRequest = _debounce(updateSearchRequestUndebounced, 250);
        init();
        // don't do this in "onMounted" because it'll trigger the watcher on
        //   `activeFilters.value`, leading to duplicate search requests (PALO-2992)
        function init() {
            availableFilters.value = createAvailableFilters();
            availablePrimaryFilter.value = createAvailablePrimaryFilter();
            initFromSearchRequest(props.searchRequest);
            activePrimaryFilter.value = createActivePrimaryFilter();
            if (props.name) {
                savedSearchService.value = new SavedSearchService(props.name);
                savedSearchService.value.list()
                    .then(data => savedSearchRequests.value = data);
            }
        }
        function initFromSearchRequest(searchRequest) {
            searchQuery.value = searchRequest.query || null;
            activeFilters.value = createActiveFilters(searchRequest);
        }
        watch(() => searchQuery.value, onSearchQueryChanged);
        function onSearchQueryChanged(value, oldValue) {
            if (value === oldValue) {
                return;
            }
            updateSearchRequest();
            matchingFilters.value = findMatchingFilters();
        }
        watch(() => props.filters, init, { deep: true });
        watch(() => activeFilters.value, updateSearchRequest, { deep: true });
        function selectPrimaryFilter(choice) {
            if (!availablePrimaryFilter.value) {
                return;
            }
            activePrimaryFilter.value = createActiveFilter(availablePrimaryFilter.value, 'eq', choice.value);
            updateSearchRequest();
        }
        function clearPrimaryFilter() {
            if (!availablePrimaryFilter.value) {
                return;
            }
            activePrimaryFilter.value = null;
            updateSearchRequest();
        }
        function blur() {
            active.value = false;
            selectedFilter.value = null;
            showAvailableFiltersDropdown.value = false;
        }
        function filterTakesValue(filter) {
            return searchFilterOperatorTakesValue(filter.operator);
        }
        /**
         * Some filters can be combined in order to achieve an OR filter for the same
         * attribute and operator and multiple values.
         */
        function shouldCombineFilters(activeFilter, filter) {
            return (activeFilter.filter.input === 'text'
                || activeFilter.filter.input === 'number'
                || activeFilter.filter.input === 'currency'
                || activeFilter.filter.input === 'email'
                || activeFilter.filter.input === 'entity')
                && filter.attribute === activeFilter.attribute
                && filter.operator === activeFilter.operator;
        }
        function updateSearchRequestUndebounced() {
            props.searchRequest.query = (searchQuery.value || '').trim() === '' ? null : (searchQuery.value || '').trim();
            const activeFiltersLocal = [];
            if (activePrimaryFilter.value) {
                activeFiltersLocal.push(activePrimaryFilter.value);
            }
            activeFilters.value.forEach(f => activeFiltersLocal.push(f));
            const filters = [];
            activeFiltersLocal
                .filter(af => !filterTakesValue(af) || af.value !== undefined)
                .forEach(af => {
                const values = isArray(af.value)
                    ? af.value.map(v => prepareFilterValue(v, af.filter))
                    : [prepareFilterValue(af.value, af.filter)];
                const filter = filters.find(f => shouldCombineFilters(af, f))
                    || {
                        attribute: af.attribute,
                        operator: af.operator,
                        values: [],
                    };
                values.forEach(value => filter.values.push(value));
                if (filters.indexOf(filter) === -1) {
                    filters.push(filter);
                }
            });
            props.searchRequest.filters = filters;
            emit('update', props.searchRequest);
        }
        function onSearch() {
            emit('update', props.searchRequest);
        }
        function addFilter(filter) {
            const existing = activeFilters.value.find(f => f.attribute === filter.attribute && !f.value);
            if (existing) {
                selectedFilter.value = existing;
                return;
            }
            const defaultValue = filter.type === 'bool' ? true : undefined;
            const activeFilter = createActiveFilter(filter, filter.operators[0], defaultValue);
            activeFilters.value.push(activeFilter);
            selectedFilter.value = activeFilter;
            if (matchingFilterHighlighted.value !== null) {
                matchingFilterHighlighted.value = null;
                searchQuery.value = null;
            }
            focusLastFilterOrSearchInput();
            updateSearchRequest();
            showAvailableFiltersDropdown.value = false;
        }
        function addFilterAndClearQuery(filter, ev) {
            if (ev) {
                // since we're handling this (enter) event, we want to stop other
                //   components from doing so, and prevent form submit.
                ev.stopPropagation();
                ev.preventDefault();
            }
            addFilter(filter);
            searchQuery.value = null;
        }
        function removeFilter(filter) {
            if (filter === selectedFilter.value) {
                deselectFilter();
            }
            activeFilters.value.splice(activeFilters.value.indexOf(filter), 1);
            updateSearchRequest();
        }
        function onSelectFilter(filter) {
            selectedFilter.value = filter;
        }
        function onRemoveFilter(filter) {
            removeFilter(filter);
            focusLastFilterOrSearchInput();
        }
        function onDeleteKeySearchQuery() {
            if (searchQuery.value === '') {
                searchQuery.value = null;
            }
        }
        function deselectFilter() {
            selectedFilter.value = null;
        }
        function focusLastFilterOrSearchInput() {
            if (activeFilters.value.length > 0) {
                selectedFilter.value = activeFilters.value[activeFilters.value.length - 1];
                return;
            }
            focusSearchInput();
        }
        function focusSearchInput() {
            searchInput.value && searchInput.value.focus();
        }
        function click(event) {
            active.value = true;
            if (event.target.classList.contains('search-filters__main')) {
                focusSearchInput();
            }
        }
        function resetSearchRequest() {
            activeFilters.value = [];
            selectedFilter.value = null;
            searchQuery.value = null;
            updateSearchRequest();
        }
        function saveSearch(name) {
            showSaveModal.value = false;
            if (savedSearchService.value) {
                savedSearchService.value.save(name, props.searchRequest)
                    .then(() => {
                    savedSearchService.value?.list()
                        .then(data => savedSearchRequests.value = data);
                });
            }
        }
        function loadSavedSearchRequest(savedSearchRequest) {
            initFromSearchRequest(_cloneDeep(savedSearchRequest.searchRequest));
            updateSearchRequest();
            showAvailableFiltersDropdown.value = false;
        }
        function deletedSavedSearchRequest(savedSearchRequest) {
            savedSearchService.value?.delete(savedSearchRequest)
                .then(() => {
                savedSearchService.value?.list()
                    .then(data => savedSearchRequests.value = data);
            });
        }
        function hideAvailableFiltersDropdown() {
            showAvailableFiltersDropdown.value = false;
        }
        function createAvailableFilters() {
            return createFormFieldsFilter(props.filters.filter(f => f.category !== 'primary'));
        }
        function createAvailablePrimaryFilter() {
            const primaryFilter = props.filters.find(f => f.category === 'primary');
            if (!primaryFilter) {
                return null;
            }
            if (primaryFilter.type !== 'choice') {
                throw 'Primary filter has to be of type "choice"';
            }
            return createFormFieldFilter(primaryFilter);
        }
        function createFormFieldsFilter(configs) {
            const filters = [];
            configs.forEach(config => {
                // ignore choice filters w/o choices
                if (config.type === 'choice' && config.options && config.options.length === 0 && !config.optionsUrl) {
                    return;
                }
                const filter = createFormFieldFilter(config);
                filters.push(filter);
            });
            return filters;
        }
        function createActiveFilters(searchRequest) {
            const filters = [];
            (searchRequest.filters || []).forEach(f => {
                const filter = findAvailableFilter(f.attribute);
                if (filter != null) {
                    createActiveFiltersForSearchFilter(filter, f).forEach(af => {
                        filters.push(af);
                    });
                }
            });
            return filters;
        }
        function createActivePrimaryFilter() {
            if (availablePrimaryFilter.value === null) {
                return null;
            }
            const filter = (props.searchRequest.filters || [])
                .find(f => f.attribute === availablePrimaryFilter.value.attribute);
            if (!filter) {
                return null;
            }
            const operator = filter.operator || availablePrimaryFilter.value.operators[0];
            if (!filter.values || filter.values.length === 0) {
                return null;
            }
            const value = filter.values[0];
            return createActiveFilter(availablePrimaryFilter.value, operator, value);
        }
        function createFormFieldFilter(config) {
            return {
                attribute: config.name,
                label: config.label,
                operators: config.operators || filterOperators(config.type),
                category: config.category || 'common',
                type: config.type,
                input: filterInput(config),
                choices: config.optionsUrl ? { url: config.optionsUrl } : (config.options || []),
            };
        }
        function filterInput(config) {
            if (config.options && config.options.length > 0) {
                return 'select';
            }
            const type = config.type;
            switch (type) {
                case 'choice':
                    return 'select';
                case 'entity':
                    return 'entity';
                case 'number':
                case 'currency': // TODO special currency input
                    return 'number';
                case 'bool':
                    return 'bool';
                case 'date':
                    return 'date';
                case 'datetime':
                    return 'datetime';
                case 'email':
                    return 'email';
                default:
                    return 'text';
            }
        }
        function filterOperators(type) {
            switch (type) {
                case 'date':
                case 'datetime':
                    return [
                        'eq',
                        'gte',
                        'lte'
                    ];
                case 'number':
                case 'currency':
                    return [
                        'eq',
                        'gte',
                        'lte'
                    ];
                case 'bool':
                    return [
                        'eq'
                    ];
                case 'keyword':
                case 'choice':
                case 'entity':
                    return [
                        'eq',
                        'neq'
                    ];
                case 'email':
                    return [
                        'contains'
                    ];
                default:
                    return [
                        'contains_term'
                    ];
            }
        }
        function findAvailableFilter(property) {
            return availableFilters.value.find(filter => {
                return filter.attribute === property
                    // map.* properties
                    || filter.attribute === property.replace(/\.[^.]+$/g, '.*');
            }) || null;
        }
        function createActiveFiltersForSearchFilter(filter, state) {
            let operator = state.operator || filter.operators[0];
            // 'choice' and 'entity' filters support multiple values
            if ((filter.type === 'choice' || filter.type === 'entity') && isArray(state.values)) {
                return [createActiveFilter(filter, operator, state.values, state.attribute)];
            }
            // for other search filters with multiple values, we create an active filter per value
            const filters = [];
            (state.values || [filter.type === 'bool' ? false : null]).map(value => {
                filters.push(createActiveFilter(filter, operator, value, state.attribute));
            });
            return filters;
        }
        function createActiveFilter(filter, operator, value, attribute) {
            if (filter.type === 'datetime' && (typeof value) === 'string') {
                value = String(value).substring(0, 16);
            }
            return {
                id: Math.random() + "",
                filter: filter,
                attribute: attribute || filter.attribute,
                operator: operator,
                value: value
            };
        }
        function findMatchingFilters() {
            if (searchQuery.value === null || searchQuery.value.trim() === '') {
                return [];
            }
            let searchQueryLocal = searchQuery.value || '';
            return availableFilters.value.filter(filter => {
                return filter.label.toLowerCase().indexOf(searchQueryLocal.toLowerCase()) > -1
                    || filter.attribute.toLowerCase().indexOf(searchQueryLocal.toLowerCase()) > -1;
            });
        }
        function selectNextMatchingFilter(ev) {
            const index = getMatchingFilterHighlightedIndex();
            const newIndex = matchingFilters.value.length > (index + 1) ? (index + 1) : index;
            if (newIndex !== -1) {
                ev.stopPropagation();
                matchingFilterHighlighted.value = matchingFilters.value[newIndex];
                activeFiltersRefs.value[newIndex].scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                });
            }
        }
        function selectPrevMatchingFilter(ev) {
            const index = getMatchingFilterHighlightedIndex();
            const newIndex = index > 0 ? index - 1 : index;
            if (newIndex !== -1) {
                ev.stopPropagation();
                matchingFilterHighlighted.value = matchingFilters.value[newIndex];
                activeFiltersRefs.value[newIndex].scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                });
            }
        }
        function getMatchingFilterHighlightedIndex() {
            return matchingFilterHighlighted.value === null
                ? -1
                : matchingFilters.value.indexOf(matchingFilterHighlighted.value);
        }
        function prepareFilterValue(value, filter) {
            if (filter.type == 'datetime') {
                if (typeof value === 'string'
                    // e.g. "today[Europe/Zurich]", "last_week[...]"
                    && value.match(/^[a-z_\d?]+\[/)) {
                    return value;
                }
                return value + ':00.000[' + env.timeZone + "]";
            }
            return value;
        }
        function isArray(value) {
            return Object.prototype.toString.call(value) === '[object Array]';
        }
        function isFiltered() {
            const hasQuery = !!(searchQuery.value && searchQuery.value.trim());
            const hasFilterWithValue = activeFilters.value
                .filter(f => {
                if (!filterTakesValue(f)) {
                    return true;
                }
                if (isArray(f.value)) {
                    // @ts-ignore
                    return f.value.length > 0;
                }
                return f.value || f.value === false;
            }).length > 0;
            return hasQuery || hasFilterWithValue;
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", {
                class: _normalizeClass(["search-filters", {
                        'search-filters--active': active.value,
                        'search-filters--filter-dropdown-open': showAvailableFiltersDropdown.value,
                        'search-filters--filtered': filtered.value,
                    }]),
                onClick: click,
                onKeydown: [
                    _cache[12] || (_cache[12] = _withKeys(
                    //@ts-ignore
                    (...args) => (_unref(updateSearchRequest) && _unref(updateSearchRequest)(...args)), ["enter"])),
                    _withKeys(focusSearchInput, ["esc"])
                ],
                ref_key: "root",
                ref: root,
                tabindex: "1"
            }, [
                (availablePrimaryFilter.value)
                    ? (_openBlock(), _createBlock(SearchFiltersPrimaryFilter, {
                        key: 0,
                        filter: availablePrimaryFilter.value,
                        state: activePrimaryFilter.value,
                        onSelect: selectPrimaryFilter,
                        onClear: clearPrimaryFilter
                    }, null, 8, ["filter", "state"]))
                    : _createCommentVNode("", true),
                _cache[22] || (_cache[22] = _createTextVNode()),
                _createElementVNode("div", _hoisted_1, [
                    (!__props.noSearchQuery)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                            _withDirectives(_createElementVNode("input", {
                                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((searchQuery).value = $event)),
                                class: "search-filters__input",
                                placeholder: __props.searchQueryPlaceholder ?? _ctx.$t('search.placeholder'),
                                ref_key: "searchInput",
                                ref: searchInput,
                                name: "query",
                                onFocus: _cache[1] || (_cache[1] = ($event) => (selectedFilter.value = null)),
                                onKeydown: [
                                    _cache[2] || (_cache[2] = _withKeys(_withModifiers(($event) => (selectNextMatchingFilter($event)), ["exact", "prevent"]), ["down"])),
                                    _cache[3] || (_cache[3] = _withKeys(_withModifiers(($event) => (selectPrevMatchingFilter($event)), ["exact", "prevent"]), ["up"])),
                                    _cache[4] || (_cache[4] = _withKeys(_withModifiers(($event) => (matchingFilterHighlighted.value && addFilterAndClearQuery(matchingFilterHighlighted.value, $event)), ["exact"]), ["enter"])),
                                    _cache[5] || (_cache[5] = _withKeys(_withModifiers(($event) => (onDeleteKeySearchQuery()), ["exact"]), ["delete"])),
                                    _cache[6] || (_cache[6] = _withKeys(_withModifiers(($event) => (matchingFilters.value.length = 0), ["exact"]), ["esc"]))
                                ],
                                autocomplete: "off"
                            }, null, 40, _hoisted_3), [
                                [_vModelText, searchQuery.value]
                            ]),
                            _cache[13] || (_cache[13] = _createTextVNode()),
                            _createVNode(Icon, {
                                name: "search",
                                onClick: onSearch
                            }),
                            _cache[14] || (_cache[14] = _createTextVNode()),
                            (active.value && matchingFilters.value.length > 0)
                                ? (_openBlock(), _createElementBlock("ul", _hoisted_4, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(matchingFilters.value, (filter, index) => {
                                        return (_openBlock(), _createElementBlock("li", {
                                            key: index,
                                            ref_for: true,
                                            ref: (el) => { activeFiltersRefs.value[index] = el; },
                                            onClick: _withModifiers(($event) => (addFilterAndClearQuery(filter)), ["prevent"]),
                                            class: _normalizeClass(["search-properties__item", { 'search-properties__item--active': filter === matchingFilterHighlighted.value, 'search-properties__item--special': filter.category === 'special' }])
                                        }, [
                                            _createTextVNode(_toDisplayString(filter.label) + " ", 1),
                                            _createElementVNode("span", _hoisted_6, _toDisplayString(_ctx.$t('dataType.' + filter.type)), 1)
                                        ], 10, _hoisted_5));
                                    }), 128))
                                ]))
                                : _createCommentVNode("", true)
                        ]))
                        : _createCommentVNode("", true),
                    _cache[20] || (_cache[20] = _createTextVNode()),
                    _createElementVNode("div", _hoisted_7, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(activeFilters.value, (filter, index) => {
                            return (_openBlock(), _createBlock(SearchFilterActive, {
                                key: index,
                                filter: filter,
                                selected: selectedFilter.value === filter,
                                "disable-removal": __props.disableFilterControls,
                                onSelect: ($event) => (onSelectFilter(filter)),
                                onRemove: ($event) => (onRemoveFilter(filter))
                            }, null, 8, ["filter", "selected", "disable-removal", "onSelect", "onRemove"]));
                        }), 128)),
                        _cache[18] || (_cache[18] = _createTextVNode()),
                        (!__props.disableFilterControls && availableFilters.value.length > 0 || savedSearchRequests.value.length > 0)
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 0,
                                class: _normalizeClass(["search-filters__filters-toggle", { 'search-filter__filters-toggle--active': showAvailableFiltersDropdown.value }]),
                                ref_key: "searchFiltersDropdown",
                                ref: searchFiltersDropdown
                            }, [
                                _createElementVNode("a", {
                                    onClick: _cache[7] || (_cache[7] = _withModifiers(($event) => (showAvailableFiltersDropdown.value = !showAvailableFiltersDropdown.value), ["prevent"])),
                                    class: "search-filters__filter-button",
                                    href: ""
                                }, [
                                    _createVNode(Icon, { name: "filter" }),
                                    _createTextVNode(" " + _toDisplayString(_ctx.$t('search.filters')), 1)
                                ]),
                                _cache[17] || (_cache[17] = _createTextVNode()),
                                (showAvailableFiltersDropdown.value)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                        _createElementVNode("h5", _hoisted_9, [
                                            _createVNode(Icon, { name: "filter" }),
                                            _createTextVNode(" " + _toDisplayString(_ctx.$t('search.filters')) + " ", 1),
                                            _createElementVNode("a", {
                                                onClick: _cache[8] || (_cache[8] = _withModifiers(($event) => (showAvailableFiltersDropdown.value = false), ["prevent"])),
                                                href: ""
                                            }, [
                                                _createVNode(Icon, { name: "close" })
                                            ])
                                        ]),
                                        _cache[16] || (_cache[16] = _createTextVNode()),
                                        _createElementVNode("ul", _hoisted_10, [
                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(savedSearchRequests.value, (savedSearchRequest) => {
                                                return (_openBlock(), _createElementBlock("li", {
                                                    key: savedSearchRequest.id,
                                                    onClick: _withModifiers(($event) => (loadSavedSearchRequest(savedSearchRequest)), ["prevent"]),
                                                    class: "search-properties__item search-properties__item--saved"
                                                }, [
                                                    _createTextVNode(_toDisplayString(savedSearchRequest.name) + " ", 1),
                                                    _createElementVNode("a", {
                                                        onClick: _withModifiers(($event) => (deletedSavedSearchRequest(savedSearchRequest)), ["prevent", "stop"]),
                                                        class: "search-properties__type",
                                                        href: ""
                                                    }, [
                                                        _createVNode(Icon, { name: "delete" })
                                                    ], 8, _hoisted_12)
                                                ], 8, _hoisted_11));
                                            }), 128)),
                                            _cache[15] || (_cache[15] = _createTextVNode()),
                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableFilters.value, (filter) => {
                                                return (_openBlock(), _createElementBlock("li", {
                                                    key: filter.attribute,
                                                    onClick: _withModifiers(($event) => (addFilter(filter)), ["prevent"]),
                                                    class: _normalizeClass(["search-properties__item", ['search-properties__item--' + filter.category]])
                                                }, [
                                                    _createTextVNode(_toDisplayString(filter.label) + " ", 1),
                                                    _createElementVNode("span", _hoisted_14, _toDisplayString(_ctx.$t('dataType.' + filter.type)), 1)
                                                ], 10, _hoisted_13));
                                            }), 128))
                                        ])
                                    ]))
                                    : _createCommentVNode("", true)
                            ], 2))
                            : _createCommentVNode("", true)
                    ]),
                    _cache[21] || (_cache[21] = _createTextVNode()),
                    (filtered.value && !__props.disableFilterControls)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                            _createElementVNode("a", {
                                onClick: _cache[9] || (_cache[9] = _withModifiers(($event) => (resetSearchRequest()), ["prevent"])),
                                class: "search-filters__filter-button search-filters__filter-button--icon",
                                href: "",
                                title: _ctx.$t('search.filters_clear')
                            }, [
                                _createVNode(Icon, { name: "close" })
                            ], 8, _hoisted_16),
                            _cache[19] || (_cache[19] = _createTextVNode()),
                            _createElementVNode("a", {
                                onClick: _cache[10] || (_cache[10] = _withModifiers(($event) => (showSaveModal.value = true), ["prevent"])),
                                class: "search-filters__filter-button search-filters__filter-button--icon",
                                href: "",
                                title: _ctx.$t('search.filters_save')
                            }, [
                                _createVNode(Icon, { name: "save" })
                            ], 8, _hoisted_17)
                        ]))
                        : _createCommentVNode("", true)
                ]),
                _cache[23] || (_cache[23] = _createTextVNode()),
                (showSaveModal.value)
                    ? (_openBlock(), _createBlock(SearchSaveModal, {
                        key: 1,
                        onClose: _cache[11] || (_cache[11] = ($event) => (showSaveModal.value = false)),
                        onSave: saveSearch,
                        "search-request": __props.searchRequest
                    }, null, 8, ["search-request"]))
                    : _createCommentVNode("", true)
            ], 34));
        };
    }
});
